import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { InvoiceService } from '../../services/invoice.service';
import {
    REFACTURA_CHECK_ICON,
    REFACTURA_FILE_ICON,
    REFACTURA_GREEN,
    styleRefacturaModal,
} from '../../shared/refactura-swal';
import { filenameFromResponse, saveBlob } from '../../shared/file-download';
import { firstValueFrom, Subscription } from 'rxjs';
import Swal from 'sweetalert2';

/** Un eslabón de la cadena de refacturación devuelta por invoice/show. */
interface RefacturaChainEntry {
    id: number;
    folio: string;
    uuid_sat: string | null;
    status: string;
    total: number;
    issued_at: string | null;
    cancelled_at: string | null;
    is_current: boolean;
    is_refactura: boolean;
}

@Component({
    selector: 'app-invoice-view',
    templateUrl: './invoice-view.component.html',
    styleUrls: ['./invoice-view.component.css']
})
export class InvoiceViewComponent implements OnInit, OnDestroy {
    folio: string = '';
    invoice: any = null;
    service: any = null;
    loading = true;

    /** Cadena de refacturación (vacía cuando la factura no tiene historial). */
    refacturaChain: RefacturaChainEntry[] = [];
    isRefactura = false;
    historyOpen = false;

    pdfUrl: SafeResourceUrl | null = null;
    pdfLoading = false;
    pdfError = false;
    private pdfObjectUrl: string | null = null;
    private routeSub?: Subscription;

    /** Pasos que se van palomeando mientras se arma el borrador de refacturación. */
    private readonly draftSteps = [
        'Copiando información de la factura',
        'Relacionando CFDI (Sustitución)',
        'Copiando Carta Porte y viajes',
        'Preparando borrador…'
    ];
    private draftStepTimers: number[] = [];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private sanitizer: DomSanitizer,
        private invoiceService: InvoiceService
    ) {}

    get isCancelled(): boolean {
        return this.invoice?.status === 'Cancelada';
    }

    /** Sólo hay historial que mostrar si la factura participó en una refacturación. */
    get hasHistory(): boolean {
        return this.refacturaChain.length > 1;
    }

    toggleHistory(): void {
        this.historyOpen = !this.historyOpen;
    }

    /**
     * Etiqueta de estado de cada eslabón. "Refacturada" gana sobre "Vigente":
     * marca que ese CFDI nació como sustituto de otro.
     */
    badgeFor(entry: RefacturaChainEntry): { label: string; kind: string } {
        if (entry.status === 'Cancelada') return { label: 'Cancelada', kind: 'danger' };
        if (entry.status === 'En proceso de cancelación') return { label: 'En proceso', kind: 'neutral' };
        if (entry.is_refactura) return { label: 'Refacturada', kind: 'warning' };
        return { label: entry.status, kind: 'success' };
    }

    /** Fecha relevante del eslabón: la de cancelación si la hay, si no la de emisión. */
    dateFor(entry: RefacturaChainEntry): string | null {
        return entry.cancelled_at ?? entry.issued_at;
    }

    openChainEntry(entry: RefacturaChainEntry): void {
        if (entry.is_current || !entry.folio) return;
        this.router.navigate(['/service/list/folio-' + entry.folio + '/view']);
    }

    ngOnInit(): void {
        // Se escucha el parámetro en vez de leer el snapshot: al saltar de una
        // factura a otra del historial, Angular reutiliza este componente y
        // ngOnInit no se vuelve a ejecutar.
        this.routeSub = this.route.paramMap.subscribe(params => {
            this.folio = params.get('folio') ?? '';
            this.loadInvoice();
        });
    }

    private loadInvoice(): void {
        this.revokePdfUrl();
        this.invoice = null;
        this.service = null;
        this.refacturaChain = [];
        this.isRefactura = false;
        this.historyOpen = false;
        this.pdfError = false;
        this.loading = true;

        if (!this.folio) {
            this.loading = false;
            return;
        }

        this.invoiceService.show(this.folio).subscribe({
            next: (res: any) => {
                this.invoice = res?.invoice ?? null;
                this.service = res?.service ?? null;
                this.refacturaChain = res?.refacturacion?.chain ?? [];
                this.isRefactura = !!res?.refacturacion?.is_refactura;
                this.loading = false;
                if (this.invoice?.id && this.invoice?.pdf_path) {
                    this.loadPdfPreview();
                }
            },
            error: () => { this.loading = false; }
        });
    }

    ngOnDestroy(): void {
        this.revokePdfUrl();
        this.clearDraftStepTimers();
        this.routeSub?.unsubscribe();
    }

    private loadPdfPreview(): void {
        this.pdfLoading = true;
        this.pdfError = false;
        this.invoiceService.downloadPdf(this.invoice.id).subscribe({
            next: (res) => {
                if (!res.body) { this.pdfError = true; this.pdfLoading = false; return; }
                this.revokePdfUrl();
                this.pdfObjectUrl = window.URL.createObjectURL(res.body);
                this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfObjectUrl);
                this.pdfLoading = false;
            },
            error: () => { this.pdfError = true; this.pdfLoading = false; }
        });
    }

    private revokePdfUrl(): void {
        if (this.pdfObjectUrl) {
            window.URL.revokeObjectURL(this.pdfObjectUrl);
            this.pdfObjectUrl = null;
        }
        this.pdfUrl = null;
    }

    goBack(): void {
        if (window.history.length > 1) {
            this.location.back();
        } else {
            this.router.navigate(['/service/list']);
        }
    }

    onCancelInvoice(): void {
        if (!this.folio || this.invoice?.status === 'Cancelada' || this.invoice?.status === 'En proceso de cancelación') return;

        Swal.fire({
            title: 'Cancelar factura',
            html: '<span style="color:#545454;font-size:18px;font-family:Lato,sans-serif">¿Estás seguro de cancelar la factura actual?</span>',
            showCancelButton: true,
            showCloseButton: true,
            confirmButtonText: 'Continuar',
            cancelButtonText: 'Cancelar',
            buttonsStyling: false,
            didOpen: () => {
                const popup = Swal.getPopup();
                if (popup) {
                    popup.style.borderRadius = '16px';
                    popup.style.width = '526px';
                    popup.style.height = '240px';
                }
                const titleEl = Swal.getTitle();
                if (titleEl) {
                    titleEl.style.color = '#545454';
                    titleEl.style.fontSize = '20px';
                    titleEl.style.fontFamily = 'Poppins, sans-serif';
                    titleEl.style.fontWeight = 'bold';
                }
                const confirmBtn = Swal.getConfirmButton();
                const cancelBtn = Swal.getCancelButton();
                if (confirmBtn) {
                    confirmBtn.style.cssText = 'background:#FFFFFF;color:#00A0E1;border:1px solid #00A0E1;border-radius:0.5rem;padding:0 16px;height:40px;width:190px;margin:0 6px;font-weight:500;';
                }
                if (cancelBtn) {
                    cancelBtn.style.cssText = 'background:#00A0E1;color:#FFFFFF;border:1px solid #00A0E1;border-radius:0.5rem;padding:0 16px;height:40px;width:190px;margin:0 6px;font-weight:500;';
                }
            }
        }).then(result => {
            if (result.isConfirmed) {
                this.showCancelMotiveModal();
            }
        });
    }

    private showCancelMotiveModal(): void {
        const stamped = !!this.invoice?.uuid_sat;
        const text = stamped
            ? 'Se cancelará ante el SAT vía SW. Selecciona el motivo de cancelación.'
            : 'Esta factura no está timbrada; se marcará como cancelada localmente.';

        Swal.fire({
            title: '¿Cancelar factura?',
            html: `
                <p style="margin-bottom:10px">${text}</p>
                <select id="cancel-motive" class="swal2-select" style="display:block;width:100%;margin:0;padding:6px 8px;font-size:14px">
                    <option value="02">02 — Comprobante emitido con errores sin relación</option>
                    <option value="01">01 — Comprobante emitido con errores con relación (sustitución)</option>
                    <option value="03">03 — No se llevó a cabo la operación</option>
                    <option value="04">04 — Operación nominativa relacionada en factura global</option>
                    <option value="otra">Otra</option>
                </select>
                <input id="cancel-uuid" class="swal2-input" style="display:none;margin:8px 0 0"
                       placeholder="UUID del CFDI que lo sustituye" />
                <textarea id="cancel-note" class="swal2-textarea" style="display:none;margin:8px 0 0"
                          placeholder="Describe el motivo de cancelación"></textarea>
            `,
            showCancelButton: true,
            confirmButtonText: 'Sí, cancelar',
            cancelButtonText: 'No',
            didOpen: () => {
                const motiveEl = document.getElementById('cancel-motive') as HTMLSelectElement | null;
                const uuidEl = document.getElementById('cancel-uuid') as HTMLInputElement | null;
                const noteEl = document.getElementById('cancel-note') as HTMLTextAreaElement | null;
                const sync = () => {
                    if (uuidEl) uuidEl.style.display = motiveEl?.value === '01' ? 'block' : 'none';
                    if (noteEl) noteEl.style.display = motiveEl?.value === 'otra' ? 'block' : 'none';
                };
                motiveEl?.addEventListener('change', sync);
                sync();
            },
            preConfirm: () => {
                const motive = (document.getElementById('cancel-motive') as HTMLSelectElement | null)?.value ?? '02';
                const uuidReplace = (document.getElementById('cancel-uuid') as HTMLInputElement | null)?.value?.trim() ?? '';
                const note = (document.getElementById('cancel-note') as HTMLTextAreaElement | null)?.value?.trim() ?? '';
                if (motive === '01' && !uuidReplace) {
                    Swal.showValidationMessage('El motivo 01 requiere el UUID del CFDI sustituto.');
                    return false;
                }
                if (motive === 'otra' && !note) {
                    Swal.showValidationMessage('Describe el motivo de cancelación.');
                    return false;
                }
                return { motive, uuid_replace: uuidReplace || undefined, note: note || undefined };
            }
        }).then(result => {
            if (!result.isConfirmed || !result.value) return;
            const payload = result.value as { motive: string; uuid_replace?: string; note?: string };
            this.invoiceService.cancel(this.folio, payload).subscribe({
                next: (res) => {
                    this.invoice = res?.invoice ?? this.invoice;
                    // Si quedó cancelada, recarga el preview para mostrar el PDF
                    // con la marca de agua "CANCELADA" que ahora sirve el backend.
                    if (this.isCancelled && this.invoice?.id && this.invoice?.pdf_path) {
                        this.loadPdfPreview();
                    }
                    if (!res?.sat) {
                        Swal.fire('Cancelada', 'La factura fue cancelada localmente.', 'success');
                    } else if (res?.confirmed) {
                        Swal.fire('Cancelada', 'La factura fue cancelada ante el SAT.', 'success');
                    } else {
                        Swal.fire(
                            'En proceso',
                            `La solicitud de cancelación se envió al SAT (código ${res?.sat_code ?? 's/n'}). Queda en proceso hasta que el receptor la acepte o el SAT la confirme.`,
                            'info'
                        );
                    }
                },
                error: (err) => {
                    const msg = err?.error?.error ?? 'No se pudo cancelar la factura.';
                    Swal.fire('Error al cancelar', msg, 'error');
                }
            });
        });
    }

    onRefacturar(): void {
        Swal.fire({
            title: 'Refacturar Factura',
            icon: 'info',
            iconHtml: REFACTURA_FILE_ICON,
            html: '<span style="color:#545454;font-size:18px;font-family:Lato,sans-serif">Se generará una nueva factura basada en este CFDI. La factura original permanecerá vigente hasta que la nueva sea timbrada correctamente.</span>',
            showCancelButton: true,
            showCloseButton: true,
            confirmButtonText: 'Continuar',
            cancelButtonText: 'Cancelar',
            buttonsStyling: false,
            reverseButtons: true,
            didOpen: () => styleRefacturaModal()
        }).then(result => {
            if (result.isConfirmed) {
                this.showDraftProgressModal();
            }
        });
    }

    /**
     * Modal de progreso "Creando borrador": muestra el checklist de lo que se va
     * copiando del CFDI original mientras se arma el borrador de la refacturación.
     */
    private showDraftProgressModal(): void {
        const stepsHtml = this.draftSteps.map((label, i) => `
            <div style="display:flex;align-items:center;gap:12px">
                <span id="rf-step-icon-${i}" style="display:inline-flex;flex:0 0 18px">${this.stepCheckIcon('#D9D9D9')}</span>
                <span id="rf-step-label-${i}" style="color:#B5B5B5;font-family:Lato,sans-serif;font-size:16px;line-height:1.2">${label}</span>
            </div>`).join('');

        Swal.fire({
            title: 'Creando borrador',
            icon: 'info',
            iconHtml: REFACTURA_CHECK_ICON,
            html: `
                <div style="display:inline-flex;flex-direction:column;gap:14px;text-align:left;margin:4px auto 0">${stepsHtml}</div>
                <div style="margin-top:22px;color:#9A9A9A;font-family:Lato,sans-serif;font-size:13px;text-align:center">
                    Esto puede tardar unos segundos…
                </div>`,
            showConfirmButton: false,
            showCancelButton: false,
            showCloseButton: true,
            allowOutsideClick: false,
            didOpen: () => {
                styleRefacturaModal();
                this.runDraftCreation();
            },
            willClose: () => this.clearDraftStepTimers()
        });
    }

    /**
     * Pide el borrador al backend mientras el checklist avanza. Se espera a que
     * terminen las dos cosas —petición y recorrido visual— para que los pasos
     * alcancen a leerse aunque la respuesta llegue de inmediato.
     */
    private runDraftCreation(): void {
        const paced = new Promise<void>(resolve => {
            this.draftSteps.forEach((_, i) => {
                this.draftStepTimers.push(window.setTimeout(() => {
                    this.markDraftStepDone(i);
                    if (i === this.draftSteps.length - 1) resolve();
                }, 600 * (i + 1)));
            });
        });

        const draft = firstValueFrom(this.invoiceService.refacturarDraft(this.folio));

        Promise.all([paced, draft]).then(([, refacturaDraft]) => {
            Swal.close();

            // Servicio que ocupará el concepto principal del formulario: el del
            // primer concepto del CFDI original.
            const principal = refacturaDraft.items?.find(i => i.service_id != null);
            const fallback = refacturaDraft.services?.[0];
            const serviceId = principal?.service_id ?? fallback?.id ?? null;
            const serviceFolio = principal?.folio ?? fallback?.folio ?? null;

            if (serviceId == null) {
                Swal.fire('Refacturar', 'La factura no tiene servicios asociados que se puedan refacturar.', 'warning');
                return;
            }

            // El folio de la factura original va en la URL para que el borrador
            // sobreviva a un refresh; el payload viaja en state para no repetir
            // la petición al navegar.
            this.router.navigate(['/service/bill-service'], {
                queryParams: {
                    folio: serviceFolio,
                    id: serviceId,
                    client: this.invoice?.client?.business_name ?? this.invoice?.client?.name ?? null,
                    refacturar: this.folio,
                },
                state: { refacturaDraft }
            });
        }).catch((err: any) => {
            Swal.close();
            const msg = err?.error?.error ?? 'No se pudo preparar el borrador de refacturación.';
            Swal.fire('Error al refacturar', msg, 'error');
        });
    }

    private markDraftStepDone(index: number): void {
        const iconEl = document.getElementById(`rf-step-icon-${index}`);
        if (iconEl) {
            iconEl.innerHTML = this.stepCheckIcon(REFACTURA_GREEN);
        }
        const labelEl = document.getElementById(`rf-step-label-${index}`);
        if (labelEl) {
            labelEl.style.color = '#545454';
        }
    }

    private clearDraftStepTimers(): void {
        this.draftStepTimers.forEach(id => window.clearTimeout(id));
        this.draftStepTimers = [];
    }

    /** Palomita de cada paso; gris mientras está pendiente, verde al completarse. */
    private stepCheckIcon(color: string): string {
        return `
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="m3.5 9.5 3.8 3.8L14.5 5" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>`;
    }

    onDownloadXml(): void {
        if (!this.invoice?.id) return;
        this.invoiceService.downloadXml(this.invoice.id).subscribe({
            next: (res) => saveBlob(res.body, filenameFromResponse(res, `${this.invoice.uuid_sat ?? this.invoice.folio}.xml`)),
            error: () => Swal.fire('XML', 'No se pudo descargar el XML.', 'error'),
        });
    }

    onDownloadPdf(): void {
        if (!this.invoice?.id) return;
        this.invoiceService.downloadPdf(this.invoice.id).subscribe({
            next: (res) => saveBlob(res.body, filenameFromResponse(res, `${this.invoice.uuid_sat ?? this.invoice.folio}.pdf`)),
            error: () => Swal.fire('PDF', 'No se pudo descargar el PDF.', 'error'),
        });
    }

}
